/**
 * Default map of backend error codes (from the JSON error envelope) to i18n
 * messages. Intentionally EMPTY in the shared lib: error codes are NOT globally
 * unique across backends, and this lib is consumed by more than one app
 * (docs/portal-id, portal-safeapp, ...). A code like `50002` can mean different
 * things per backend, so mapping it here would risk a cross-app collision
 * (wrong message for the wrong app).
 *
 * Each app owns its code→message map and injects it, scoped to its own backend
 * namespace, via the `errorCodeMessages` prop on `ErrorBoundaryWrapper`. Each
 * entry is `{ key, fallback }`: an i18n key plus a versioned English fallback
 * used when the key is missing from the loaded resources.
 */
export const ERROR_CODE_MESSAGES = {};

/**
 * Parses the raw body carried by an {@link HttpError} and returns the backend
 * error envelope `{ code, description }` when present.
 *
 * `HttpError.handleStatusCode` stores the response body (via `response.text()`)
 * in `error.message`, so for a JSON error response the message is a stringified
 * `{ "error": { "code": number, "description": string } }`. This helper safely
 * parses it and returns `null` when the body is not the expected envelope (e.g.
 * plain-text messages or non-JSON bodies), so callers can fall back to generic
 * handling.
 *
 * @param {string} rawBody - The raw error body (typically `error.message`).
 * @returns {{ code?: number, description?: string } | null}
 */
export function parseErrorEnvelope(rawBody) {
  if (typeof rawBody !== 'string') return null;

  try {
    const parsed = JSON.parse(rawBody);
    const error = parsed?.error;
    if (error && (error.code !== undefined || error.description !== undefined)) {
      return { code: error.code, description: error.description };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolves the i18n key + fallback for a given backend error code, or `null`
 * when the code has no specific message mapped.
 *
 * @param {number} code - The backend error code (e.g. `50002`).
 * @param {Object} [messages=ERROR_CODE_MESSAGES] - The code→message map to look
 *   up against. Apps can pass their own (via `ErrorBoundaryWrapper`'s
 *   `errorCodeMessages` prop) to scope codes to their own backend namespace.
 * @returns {{ key: string, fallback: string } | null}
 */
export function getMessageForErrorCode(code, messages = ERROR_CODE_MESSAGES) {
  return messages?.[code] ?? null;
}
